import { BrowserCacheLocation, IPublicClientApplication, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';

type FridaDeploymentEnvironment = 'development' | 'test' | 'production';

declare global {
  interface Window {
    __fridaAuthConfig?: Partial<typeof defaultFridaAuthConfig>;
  }
}

const defaultFridaAuthConfig = {
  clientId: '6242b232-1389-4a51-9bd8-9aef2eac72ca',
  tenantId: '9dfb1a05-5f1d-449a-8960-62abcb479e7d',
  authority: 'https://login.microsoftonline.com/9dfb1a05-5f1d-449a-8960-62abcb479e7d',
  localApiBaseUrl: 'https://localhost:7164',
  developmentApiBaseUrl: 'https://wa-np-d-frida.ase-np-d-treasury.appserviceenvironment.net',
  testApiBaseUrl: '',
  productionApiBaseUrl: '',
  deploymentEnvironment: 'development' as FridaDeploymentEnvironment,
  apimBaseUrl: '',
  scope: 'api://ba390ee6-818b-42da-a326-3b2c6acde365/access_as_user',
};

const configuredFridaAuthConfig = {
  ...defaultFridaAuthConfig,
  ...window.__fridaAuthConfig,
};

const isLocalhost =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const configuredEnvironment = configuredFridaAuthConfig.deploymentEnvironment;
const selectedApiBaseUrl = isLocalhost
  ? configuredFridaAuthConfig.localApiBaseUrl
  : configuredEnvironment === 'production'
    ? configuredFridaAuthConfig.productionApiBaseUrl || configuredFridaAuthConfig.developmentApiBaseUrl
    : configuredEnvironment === 'test'
      ? configuredFridaAuthConfig.testApiBaseUrl || configuredFridaAuthConfig.developmentApiBaseUrl
    : configuredFridaAuthConfig.developmentApiBaseUrl;

export const fridaAuthConfig = {
  ...configuredFridaAuthConfig,
  apiBaseUrl: selectedApiBaseUrl,
};

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string): void {
  if (logLevel >= LogLevel.Warning) {
    console.warn(message);
  }
}

export function msalInstanceFactory(): IPublicClientApplication {
  const absoluteRedirectUri = `${window.location.origin}/auth`;
  const absolutePostLogoutRedirectUri = `${window.location.origin}/`;

  return new PublicClientApplication({
    auth: {
      clientId: fridaAuthConfig.clientId,
      authority: fridaAuthConfig.authority,
      redirectUri: absoluteRedirectUri,
      postLogoutRedirectUri: absolutePostLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  const scope = [fridaAuthConfig.scope];
  const apiBaseUrl = fridaAuthConfig.apiBaseUrl.replace(/\/+$/, '');
  const apimBaseUrl = fridaAuthConfig.apimBaseUrl.replace(/\/+$/, '');

  protectedResourceMap.set(apiBaseUrl, scope);

  if (apimBaseUrl) {
    protectedResourceMap.set(apimBaseUrl, scope);
  }

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [fridaAuthConfig.scope],
    },
    loginFailedRoute: '/unauthorized',
  };
}
